/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Pribyl (pribyl@cadwork.cz)
 *
 */

import IndexedFacetSet from "App/geometry/objects_geometry/indexed_face_set";
import RenderObject from "App/geometry/objects/object";
import VertexDataDescriptor from "App/geometry/objects_geometry/vertex_data_descriptor";
import Factory from "App/geometry/objects_geometry/factory";
import {mat4, vec3} from "gl-matrix";
import AbstractGeometry from "App/geometry/objects_geometry/abstract_geometry";
import BoundingBox from "App/utils/bounding_box";

export default
class OffsetedIndexedFaceSet extends IndexedFacetSet {

    public static TypeName: string = "offsetedIndexedFaceSet";

    public positionOffsetsDesc: VertexDataDescriptor = null;

    constructor(aObject: RenderObject) {
        super(aObject);
    }


    public static create(aObject: RenderObject) {
        return new OffsetedIndexedFaceSet(aObject);
    }

    public clear() {
        super.clear();

        this.positionOffsetsDesc = null;
    }

    public updatePositions(aPositions) {
        IndexedFacetSet.prototype.updatePositions.call(this, aPositions);

        let offsets = new Float32Array(this.positionsDesc.data.length);
        for (let i = 0; i < this.positionsDesc.data.length; i += 3) {
            offsets[i + 0] = this.properties.offset[0];
            offsets[i + 1] = this.properties.offset[1];
            offsets[i + 2] = this.properties.offset[2];
        }
    }

    public computeBBox() {
        let point = vec3.create();
        let bbox = new BoundingBox();
        for (let i = 0; i < this.positionsDesc.data.length; i += 3) {
            vec3.set(
                point,
                this.positionsDesc.data[i + 0] + this.properties.offset[0],
                this.positionsDesc.data[i + 1] + this.properties.offset[1],
                this.positionsDesc.data[i + 2] + this.properties.offset[2]
            );
            bbox.extendByPoint(point);
        }

        return bbox;
    }

    /***
     * Override transform as we need to transform posisiton offsets not positions.
     * @param aTransformation transformation matrix
     */
    public transform(aTransformation: mat4) {
        this.positionOffsetsDesc.data = AbstractGeometry.transformArray(
            <Float32Array>this.positionOffsetsDesc.data,
            this.positionOffsetsDesc.nofElements,
            aTransformation
        );

        let positionAttributeSize = this.object.getPositionAttributeSize();
        let nonIndexedVertices = this.generateNonIndexedVertices(positionAttributeSize);
        this.nonIndexedPositionsDesc = new VertexDataDescriptor(
            "nonIndexedPositions",
            positionAttributeSize,
            nonIndexedVertices.length / positionAttributeSize,
            "FLOAT",
            4,
            0,
            nonIndexedVertices
        );

        // recompute bbox from the transformed vertices. Transformation of just the min and max simply does not work,
        // because the aabb can be bigger after transformation compared to the local non aabb.
        let aabb = this.computeBBox();
        vec3.copy(this.object.getProperties().bboxMin,  aabb.getMin());
        vec3.copy(this.object.getProperties().bboxMax,  aabb.getMax());
    }

    public loadData(aBuffer: Uint8Array) {
        let data: any = {
            positions: null,
            offsets: null,
            normals: null,
            texCoords: null,
            indices: null
        };

        // this ssi the billboard local geometry
        data.positions = new Float32Array(this.properties.positions);

        // this is offset of the billboard
        data.offsets = new Float32Array(this.properties.positions.length);
        for (let i = 0; i < this.properties.positions.length; i += 3) {
            data.offsets[i + 0] = this.properties.offset[0];
            data.offsets[i + 1] = this.properties.offset[1];
            data.offsets[i + 2] = this.properties.offset[2];
        }

        let repetitionTrafo = this.object.getElement().getRepetitionTransformation();
        if (repetitionTrafo) {
            data.offsets = AbstractGeometry.transformArray(
                data.offsets,
                this.properties.vertexCount,
                new Float32Array(repetitionTrafo)
            );
        }

        if (this.properties.normals !== undefined) {
            data.normals = new Float32Array(this.properties.normals);
        } else {
            data.normals = new Float32Array(this.properties.normalCount * 3);
        }

        if (repetitionTrafo) {
            let rotationMat = mat4.clone(new Float32Array(repetitionTrafo));
            rotationMat[3] = 0.0;
            rotationMat[7] = 0.0;
            rotationMat[11] = 0.0;
            rotationMat[12] = 0.0;
            rotationMat[13] = 0.0;
            rotationMat[14] = 0.0;
            rotationMat[15] = 1.0;
            data.normals = AbstractGeometry.transformArray(
                data.normals,
                this.properties.normalCount,
                rotationMat
            );
        }

        if (this.properties.texCoords !== undefined) {
            data.texCoords = new Float32Array(this.properties.texCoords);
        } else {
            data.texCoords = new Float32Array(this.properties.texCoordCount * 2);
        }

        data.indices = new Uint32Array(this.properties.indices);

        this.setDescriptors(data);

        data = null;
    }

    protected setDescriptors(aData: any) {
        super.setDescriptors(aData);

        this.positionOffsetsDesc = new VertexDataDescriptor(
            "positionOffsets",
            3,
            aData.offsets.length / 3,
            "FLOAT",
            4,
            0,
            aData.offsets
        );
    }
}

Factory.register(OffsetedIndexedFaceSet);